import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Circle } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { apiService } from "@/lib/api-service";
import { ProjectStatus } from "@employee-management/common";
import { toast } from "sonner";
import clsx from "clsx";

interface StatusDropdownProps {
  projectId: string;
  currentStatus: ProjectStatus;
  onStatusChange?: (newStatus: ProjectStatus) => void;
  canEditProject?: boolean;
}

const statusConfig = (canEditProject: boolean = false) => ({
  [ProjectStatus.ACTIVE]: {
    label: "Active",
    className: clsx(
      "bg-blue-100 text-blue-800 border-blue-200",
      canEditProject ? "hover:bg-blue-200" : "hover:bg-blue-100"
    ),
    iconColor: "text-blue-500",
  },
  [ProjectStatus.COMPLETED]: {
    label: "Completed",
    className: clsx(
      "bg-green-100 text-green-800 border-green-200",
      canEditProject ? "hover:bg-green-200" : "hover:bg-green-100"
    ),
    iconColor: "text-green-500",
  },
  [ProjectStatus.ON_HOLD]: {
    label: "On Hold",
    className: clsx(
      "bg-yellow-100 text-yellow-800 border-yellow-200",
      canEditProject ? "hover:bg-yellow-200" : "hover:bg-yellow-100"
    ),
    iconColor: "text-yellow-500",
  },
  [ProjectStatus.CANCELLED]: {
    label: "Cancelled",
    className: clsx(
      "bg-red-100 text-red-800 border-red-200",
      canEditProject ? "hover:bg-red-200" : "hover:bg-red-100"
    ),
    iconColor: "text-red-500",
  },
});

export function StatusDropdown({
  projectId,
  currentStatus,
  onStatusChange,
  canEditProject,
}: StatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: ProjectStatus) => {
      await apiService.patch(`/projects/${projectId}/status`, {
        status: newStatus,
      });
    },
    onSuccess: (_, newStatus) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({
        queryKey: ["project-revision-history", projectId],
      });
      onStatusChange?.(newStatus);
      setIsOpen(false);
    },
    onError: (error: unknown) => {
      console.error("Failed to update project status:", error);
      // You could add toast notification here
    },
  });

  const handleStatusChange = (newStatus: ProjectStatus) => {
    if (newStatus !== currentStatus) {
      toast.promise(updateStatusMutation.mutateAsync(newStatus), {
        loading: "Updating status...",
        success: "Status updated successfully",
        error: "Failed to update status",
      });
    }
  };

  const config = statusConfig(canEditProject)[currentStatus];

  if (!canEditProject) {
    return (
      <Badge
        variant="default"
        className={`cursor-default transition-opacity ${config.className}`}
      >
        <Circle className={`h-3 w-3 mr-1 ${config.iconColor} fill-current`} />
        {config.label}
      </Badge>
    );
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger>
          <Badge
            variant="default"
            className={`cursor-pointer transition-opacity ${config.className}`}
          >
            <Circle
              className={`h-3 w-3 mr-1 ${config.iconColor} fill-current`}
            />
            {config.label}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {Object.entries(statusConfig(canEditProject)).map(
            ([status, statusConfig]) => (
              <DropdownMenuItem
                key={status}
                onClick={() => handleStatusChange(status as ProjectStatus)}
                disabled={updateStatusMutation.isPending}
                className="flex items-center gap-2"
              >
                <Circle
                  className={`h-4 w-4 ${statusConfig.iconColor}`}
                  fill="currentColor"
                />
                {statusConfig.label}

                {currentStatus === status && (
                  <Check className="h-3 w-3 ml-auto" />
                )}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
